import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { money } from "@/lib/brands";
import { CheckoutDialog } from "@/components/CheckoutDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, CalendarCheck, Stethoscope, ArrowUpRight, PawPrint, Plus, Receipt, Scissors, Home } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <Card className="p-5 border-border/60 hover:border-primary/60 transition-all hover:-translate-y-0.5">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</div>
        <div className="font-primary text-3xl md:text-4xl font-bold mt-2">{value}</div>
      </div>
      <div className={`grid place-items-center w-11 h-11 rounded-xl ${accent}`}>
        <Icon className="h-5 w-5" strokeWidth={2} />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const canService = user?.role === "admin" || user?.role === "staff";
  const [stats, setStats] = useState(null);
  const [openCases, setOpenCases] = useState([]);

  const loadOpen = () => { if (canService) api.get("/dashboard/open-cases").then((r) => setOpenCases(r.data)).catch(() => {}); };
  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    loadOpen();
    // eslint-disable-next-line
  }, [user]);

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto animate-fade-in">
      {/* Hero */}
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Overview</div>
          <h1 className="font-primary text-4xl md:text-5xl font-bold tracking-tight mt-1">Dashboard</h1>
          <p className="font-accent text-2xl text-muted-foreground mt-1">caring for tails, one record at a time</p>
        </div>
        {user?.brand !== "kennelmart" && (
          <Link to="/consultations/new">
            <Button data-testid="dashboard-new-consultation-btn" size="lg" className="font-semibold">
              <Plus className="h-4 w-4 mr-2" /> New Consultation
            </Button>
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        <StatCard icon={Users} label="Total Patients" value={stats?.total_patients ?? "—"} accent="bg-primary text-primary-foreground" />
        <StatCard icon={Stethoscope} label="Today's Cases" value={stats?.today_cases ?? "—"} accent="bg-foreground text-background" />
        <StatCard icon={CalendarCheck} label="Follow-up Cases" value={stats?.follow_ups ?? "—"} accent="bg-secondary text-secondary-foreground" />
      </div>

      {canService && (
        <Card className="p-6 border-border/60 mt-8" data-testid="open-cases-widget">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-primary text-lg font-semibold flex items-center gap-2"><Receipt className="h-5 w-5 text-primary" />Open Cases</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Customers with unclosed services — settle everything in one bill.</p>
            </div>
            {openCases.length > 0 && <span className="text-xs px-2.5 py-1 rounded-full bg-accent/15 text-accent font-semibold">{openCases.length} open</span>}
          </div>
          {openCases.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">No open cases — every customer is settled. 🎉</div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {openCases.map((o, i) => (
                <div key={o.any_patient_id || o.phone || i} data-testid={`open-case-${i}`} className="rounded-2xl border border-border/60 p-4 hover:border-primary/50 transition-all">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{o.owner_name || "—"}</div>
                      <div className="text-xs text-muted-foreground truncate">{o.phone} · {o.pets.join(", ")}</div>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-secondary shrink-0">{o.open_count} open</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3 text-xs text-muted-foreground">
                    {o.grooming > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted"><Scissors className="h-3 w-3" />{o.grooming}</span>}
                    {o.boarding > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted"><Home className="h-3 w-3" />{o.boarding}</span>}
                    {o.consultation > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted"><Stethoscope className="h-3 w-3" />{o.consultation}</span>}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/60">
                    <span className="text-sm">Est. <span className="font-semibold">{money(o.total_suggested)}</span></span>
                    <CheckoutDialog patientId={o.any_patient_id} onSettled={() => { loadOpen(); api.get("/dashboard/stats").then((r) => setStats(r.data)); }}
                      trigger={<Button size="sm" data-testid={`open-case-settle-${i}`}><Receipt className="h-3.5 w-3.5 mr-1.5" />Settle</Button>} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <Card className="p-6 lg:col-span-2 border-border/60">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-primary text-lg font-semibold">Recent Clients</h3>
            <Link to="/clients" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">View all <ArrowUpRight className="h-3 w-3" /></Link>
          </div>
          <div className="divide-y divide-border">
            {(stats?.recent_clients || []).length === 0 && (
              <div className="text-sm text-muted-foreground py-8 text-center">No clients yet.</div>
            )}
            {(stats?.recent_clients || []).map((c) => (
              <Link key={c.id} to={`/clients/${c.id}`} data-testid={`dash-client-${c.id}`} className="flex items-center gap-4 py-3 hover:bg-muted/40 -mx-2 px-2 rounded-lg transition-colors">
                <div className="grid place-items-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                  <Users className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{c.owner_name}</div>
                  <div className="text-xs text-muted-foreground">{c.phone} · {c.pets_count} pet{c.pets_count === 1 ? "" : "s"}</div>
                </div>
                <div className="text-xs text-muted-foreground hidden sm:block">{new Date(c.created_at).toLocaleDateString()}</div>
              </Link>
            ))}
          </div>
        </Card>

        <div className="space-y-6">
          <Card className="p-6 border-border/60">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-primary text-lg font-semibold">Recent Activity</h3>
            </div>
            <div className="space-y-3">
              {(stats?.recent_activity || []).length === 0 && <div className="text-sm text-muted-foreground">No recent activity.</div>}
              {(stats?.recent_activity || []).map((a, i) => {
                const Icon = a.kind === "Grooming" ? Scissors : a.kind === "Boarding" ? Home : Stethoscope;
                return (
                  <div key={`${a.kind}-${a.created_at}-${a.pet_name || i}`} className="flex items-center gap-3">
                    <div className="grid place-items-center w-9 h-9 rounded-lg bg-muted text-muted-foreground shrink-0"><Icon className="h-4 w-4" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{a.pet_name || "Pet"} · {a.kind}</div>
                      <div className="text-xs text-muted-foreground">{a.owner_name} · {new Date(a.created_at).toLocaleDateString()}{a.status ? ` · ${a.status}` : ""}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="p-6 border-border/60">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-primary text-lg font-semibold">Upcoming Appointments</h3>
            </div>
            <div className="space-y-3">
              {(stats?.upcoming_appointments || []).length === 0 && (
                <div className="text-sm text-muted-foreground">No upcoming appointments.</div>
              )}
              {(stats?.upcoming_appointments || []).map((a) => (
                <div key={a.id} className="flex items-center gap-3">
                  <div className="w-12 text-center">
                    <div className="text-xs text-muted-foreground">{new Date(a.date_time).toLocaleDateString(undefined, { month: "short" }).toUpperCase()}</div>
                    <div className="font-primary text-xl font-bold">{new Date(a.date_time).getDate()}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate text-sm">{a.pet_name} · {a.reason}</div>
                    <div className="text-xs text-muted-foreground">{new Date(a.date_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · {a.owner_name}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6 border-border/60">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-primary text-lg font-semibold">Recent Reports</h3>
            </div>
            <div className="space-y-3">
              {(stats?.recent_reports || []).length === 0 && (
                <div className="text-sm text-muted-foreground">No reports yet.</div>
              )}
              {(stats?.recent_reports || []).map((r) => (
                <Link key={r.id} to={`/patients/${r.patient_id}`} className="block">
                  <div className="text-sm font-medium">{r.pet_name || "Patient"} — {r.primary_diagnosis || "Consultation"}</div>
                  <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</div>
                </Link>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
